import clsx from 'clsx';
import React, { ReactNode } from 'react';

const TableWrapper: React.FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div className="inline-grid w-full flex-col overflow-hidden lg:flex">
      <div className="-m-1.5 overflow-x-auto">
        <div className="inline-block min-w-full p-1.5 align-middle">
          <div
            className={clsx(
              'shadown-sm relative w-full overflow-hidden rounded-xl border border-border',
              className,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => {
  return (
    <table
      ref={ref}
      className={clsx('divide-stroke min-w-full divide-y', className)}
      {...props}
    />
  );
});
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={clsx('bg-card', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={clsx('divide-stroke divide-y', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={clsx(
      'bg-primary font-medium text-primary-foreground',
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr ref={ref} className={clsx('hover:bg-muted/50', className)} {...props} />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={clsx(
      'px-6 py-3 text-left text-xs font-semibold text-muted-foreground',
      className,
    )}
    scope="col"
    {...props}
  />
));
TableHead.displayName = 'TableHead';

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  parentClassName?: string;
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, parentClassName, ...props }, ref) => {
    return (
      <td
        ref={ref}
        className={clsx('h-px w-px whitespace-nowrap', parentClassName)}
        {...props}
      >
        <div
          className={clsx('px-6 py-3 text-sm text-muted-foreground', className)}
        >
          {props.children}
        </div>
      </td>
    );
  },
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={clsx('mb-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

const TableTopHeader: React.FC<{
  title?: ReactNode;
  subtitle?: string;
  children?: ReactNode;
  className?: string;
}> = ({ title, subtitle, children, className }) => {
  return (
    <div
      className={clsx(
        'grid gap-3 border-b border-border px-6 py-4 md:flex md:items-center md:justify-between',
        className,
      )}
    >
      <div>
        <h2 className="text-xl font-semibold ">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {children && (
        <div>
          <div className="inline-flex gap-x-2">{children}</div>
        </div>
      )}
    </div>
  );
};

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableTopHeader,
  TableWrapper,
};
